// ═══════════════════════════════════════════════════════════════════════
// esbData.js — Enterprise Service Bus for CLM design data
// ═══════════════════════════════════════════════════════════════════════
// Sources: CLM_ESB.pptx + CLM_ESB_Siemens.pptx
//   · 3 pillars (durable · observable · recoverable)
//   · 7-plane reference architecture
//   · 8 core building blocks
//   · 3 delivery semantics (at-most-once · at-least-once · exactly-once)
//   · 4 ACK protocols
//   · 6 tier-1 CLM flows
//   · Siemens-native integration stack (8 surfaces)
//   · 5-vendor decision matrix (Kafka recommended)
//   · 18-month roadmap
// ═══════════════════════════════════════════════════════════════════════

export const ESB_PILLARS = [
  { id: "DURABLE",     desc: "Every message persisted before any ACK",           color: "#14b8a6" },
  { id: "OBSERVABLE",  desc: "Every hop traced · every flow auditable",          color: "#14b8a6" },
  { id: "RECOVERABLE", desc: "Every failure replayable · nothing silently lost", color: "#14b8a6" },
];

export const ESB_PLANES = [
  { name: "ENDPOINTS",     desc: "Teamcenter 2512 · Opcenter EX 2507 · Opcenter APS 2510 · Industrial Edge · Insights Hub · SAP S/4HANA · Mendix",
    color: "#a78bfa", tag: "7 systems" },
  { name: "ADAPTERS",      desc: "AIG · T4S · Mendix Data Hub · Opcenter SOA REST · Industrial Information Hub · Insights Hub APIs (OAuth)",
    color: "#fb923c", tag: "per-system" },
  { name: "GATEWAY",       desc: "mTLS termination · authn (OIDC) · authz (OPA) · rate limit · cost & quota · request shaping",
    color: "#ffd600", tag: "north/south" },
  { name: "BROKER",        desc: "Persistent log · partitioned · replicated (min.insync.replicas ≥ 2) · ordered per key · 7-day default retention",
    color: "#14b8a6", tag: "the bus" },
  { name: "ROUTING",       desc: "Content-based · header-based · splitter/aggregator · transform · enrich · canonical model · saga orchestrator",
    color: "#00d4c0", tag: "e-i-p" },
  { name: "CONTROL",       desc: "Schema registry · OPA policy bundles · DLQ + replay · idempotency store · audit ledger · feature flags",
    color: "#fbbf24", tag: "governance" },
  { name: "OBSERVABILITY", desc: "OpenTelemetry traces · metrics (RED/USE) · structured logs · service maps · alerting · billing per topic",
    color: "#2dd4bf", tag: "see all" },
];

export const ESB_BUILDING_BLOCKS = [
  { num: "01", name: "BROKER",          desc: "Apache Kafka 3.7+ · MSK / Confluent Cloud · partitioned, replicated, durable",
    vendors: "Kafka · RabbitMQ · IBM MQ · Solace" },
  { num: "02", name: "API GATEWAY",     desc: "Kong / Apigee / AWS API GW · mTLS · OIDC · rate limit · OPA pre-check",
    vendors: "Kong · Apigee · AWS · NGINX+" },
  { num: "03", name: "SYSTEM ADAPTERS", desc: "Per-system connectors — AIG, T4S, Mendix, Insights Hub APIs, Industrial Information Hub",
    vendors: "Siemens AIG · T4S · Mendix · custom" },
  { num: "04", name: "SCHEMA REGISTRY", desc: "Confluent SR · Apicurio · AWS Glue SR · contract first · BACKWARD compatibility default",
    vendors: "Confluent SR · Apicurio" },
  { num: "05", name: "ROUTER",          desc: "Apache Camel · Spring Integration · Kafka Streams · KSQL · content/header-based routing",
    vendors: "Camel · Spring · Streams · Flink" },
  { num: "06", name: "TRANSFORMER",     desc: "Canonical model translation · normalization · enrichment from MDM · format conversion",
    vendors: "Camel · jOOQ · custom Java/Python" },
  { num: "07", name: "DLQ + REPLAY",    desc: "Dead-letter topic per flow · replay UI · classify · auto-replay clear cases · audit",
    vendors: "Custom on Kafka · Conduktor · Lenses" },
  { num: "08", name: "OBSERVABILITY",   desc: "OpenTelemetry spans · Prometheus metrics · Grafana dashboards · Loki/ELK logs · alerting",
    vendors: "OTel · Prometheus · Grafana · ELK" },
];

export const ESB_SEMANTICS = [
  { id: "at-most-once",  symbol: "≤1", color: "#ffd600",
    means: "Send once. If it fails, drop it. No retry.",
    useFor: "Metrics, telemetry, signals where loss is tolerable",
    how:    "Fire-and-forget · no ACKs · no persistence required",
    cost:   "Cheap · fast · but you WILL lose messages",
    example: "OEE telemetry · machine heartbeats · non-critical events" },
  { id: "at-least-once", symbol: "≥1", color: "#14b8a6",
    means: "Keep retrying until ACKed. Duplicates possible.",
    useFor: "DEFAULT for CLM business transactions",
    how:    "Persistent broker · ACK-after-commit · idempotent consumer",
    cost:   "Moderate · need idempotency · MOST flows live here",
    example: "ECN propagation · order release · confirmation · NCR" },
  { id: "exactly-once",  symbol: "=1", color: "#ff4444",
    means: "Delivered once, no duplicates, no loss.",
    useFor: "Strictly required for finance, regulated transactions",
    how:    "Transactional · idempotent · dedupe store · 2PC OR Outbox",
    cost:   "Expensive · slow · hard to operate at scale",
    example: "GL postings · invoice numbering · stock movements" },
];

export const ACK_PROTOCOLS = [
  { num: "01", chain: "PRODUCER → BROKER",
    trigger: "Producer sends message", waits: "sync write to ≥2 replicas",
    signal: "Broker returns offset and partition",
    failure: "Producer retries with idempotent producer ID — NO loss, NO duplicates",
    why: "If you skip this ACK, you're effectively at-most-once",
    color: "#a78bfa" },
  { num: "02", chain: "BROKER → PRODUCER",
    trigger: "Replicas confirm committed", waits: "All in-sync replicas have appended",
    signal: "Success status returned to producer SDK",
    failure: "Timeout = producer retries · idempotent ID prevents duplicate write",
    why: "This is what makes acks=all meaningful",
    color: "#14b8a6" },
  { num: "03", chain: "BROKER → CONSUMER",
    trigger: "Consumer polls partition", waits: "Broker returns message batch from log",
    signal: "Messages delivered to consumer poll() call",
    failure: "Network failure = consumer re-polls same offset · no harm",
    why: "At this stage no commitment yet — consumer hasn't processed",
    color: "#fb923c" },
  { num: "04", chain: "CONSUMER → BROKER",
    trigger: "Consumer finishes processing AND commits result", waits: "Idempotency check passed · downstream write complete",
    signal: "Offset commit to broker · advances consumer position",
    failure: "Crash before commit = reprocess on restart · idempotency saves us",
    why: "NEVER auto-commit · always manual commit after success",
    color: "#34d399" },
];

export const PERSISTENCE_LAYERS = [
  { id: "L1", name: "MEMORY ONLY",        color: "#ff4444",
    desc: "Message held in broker JVM heap. Disappears on crash, restart, GC pause.",
    ack: "ACK after queue insert · before disk write",
    dataLoss: "100% on broker failure",
    when: "Dev only · never production · never CLM" },
  { id: "L2", name: "PAGE CACHE + FSYNC", color: "#ffd600",
    desc: "Message written to broker disk · OS page cache flushed on commit.",
    ack: "ACK after fsync to local disk",
    dataLoss: "Loss on disk failure · OK on broker restart",
    when: "Single-broker scenarios · DEV/QA only" },
  { id: "L3", name: "REPLICATED + COMMITTED", color: "#00ff88",
    desc: "Message written and replicated to ≥ min.insync.replicas brokers across AZs.",
    ack: "ACK only after replicas confirm commit",
    dataLoss: "Survives single-AZ failure · zero data loss",
    when: "DEFAULT for all CLM production flows" },
];

export const SIEMENS_STACK = [
  { name: "AIG",                 product: "Active Integration Gateway",     color: "#a78bfa", scope: "Teamcenter ↔ everything",
    detail: "Event-driven sync from Teamcenter to MES, ERP, Cloud · ECN propagation · BOM publish",
    proto: "Event bus · REST · message broker bridges" },
  { name: "T4S",                 product: "Teamcenter Gateway for SAP",     color: "#60a5fa", scope: "Teamcenter ↔ SAP S/4HANA",
    detail: "Material master · BOM · Doc Info Record · Production Version · cost flow",
    proto: "BAPI · RFC · IDoc · SOAP" },
  { name: "OPCENTER SOA",        product: "Opcenter Service-Oriented APIs", color: "#fb923c", scope: "Opcenter EX ↔ external",
    detail: "Work order release · operation status · NCR · containment · genealogy",
    proto: "REST · XML over HTTP · WCF" },
  { name: "APS INTEROP",         product: "APS Interoperability Adapter",   color: "#fbbf24", scope: "Opcenter APS ↔ Opcenter EX",
    detail: "Schedule → work orders auto-conversion · status feedback to APS sequencer",
    proto: "REST · file-based · DB" },
  { name: "IND. INFO HUB",       product: "Industrial Information Hub",      color: "#4ade80", scope: "Edge ↔ central IT",
    detail: "Bidirectional model sync · OPC-UA aggregation · ARM device support",
    proto: "OPC-UA · MQTT · REST" },
  { name: "INSIGHTS HUB API",    product: "Insights Hub Open APIs",          color: "#2dd4bf", scope: "Cloud IoT ↔ enterprise",
    detail: "Time-series · asset model · alerts · jobs · low-code Mendix integration",
    proto: "REST · OAuth 2.0 · MQTT" },
  { name: "MENDIX DATA HUB",     product: "Mendix Data Hub",                 color: "#f472b6", scope: "Cross-system data discovery",
    detail: "Catalog all sources · low-code consume · MDM front-end · process apps",
    proto: "OData · REST · GraphQL" },
  { name: "IND. AI SUITE",       product: "Industrial AI Suite",             color: "#34d399", scope: "Edge AI lifecycle",
    detail: "Model deploy/retrain · combine image + MES + controller data · scale across sites",
    proto: "REST · MQTT · Edge App API" },
];

export const TIER1_FLOWS = [
  { num: "01", name: "ECN PROPAGATION",          tier: "TIER 1",
    route: "TC (2512) → OPC + SAP + IX",
    desc: "Engineer approves → AIG event → bus → fan-out → all systems",
    sla: "P99 < 90s · zero loss · audit linked", color: "#ff4f8b" },
  { num: "02", name: "BOM PUBLISH",              tier: "TIER 1",
    route: "TC (2512) → SAP + OPC",
    desc: "BOM committed → T4S to SAP material/STKO → Opcenter routing",
    sla: "P99 < 5 min · order of operations preserved", color: "#ff4f8b" },
  { num: "03", name: "WORK ORDER RELEASE",       tier: "TIER 1",
    route: "SAP / APS → OPC EX",
    desc: "AUFNR created in SAP → bus → APS interop OR Opcenter SOA REST → floor",
    sla: "P99 < 30s · idempotent", color: "#ff4f8b" },
  { num: "04", name: "PRODUCTION CONFIRMATION",  tier: "TIER 1",
    route: "OPC EX → SAP + IX",
    desc: "Operation complete → Opcenter outbox → bus → SAP BAPI confirm + cloud KPI",
    sla: "P99 < 30s · exactly-once on finance side", color: "#ff4f8b" },
  { num: "05", name: "NCR + CONTAINMENT",        tier: "TIER 1",
    route: "OPC EX → TC + IX",
    desc: "NCR raised → containment API → bus → TC change request + cloud alert",
    sla: "P99 < 60s · regulated audit trail", color: "#ff4f8b" },
  { num: "06", name: "OEE / TELEMETRY",          tier: "TIER 2",
    route: "Edge → IX + OPC + dashboards",
    desc: "OPC-UA tags → Industrial Information Hub → bus → Insights Hub time-series",
    sla: "P99 < 5s · at-least-once · last-write-wins per tag", color: "#fbbf24" },
];

export const VENDOR_MATRIX = [
  { criterion: "Throughput",         kafka: 5, rabbitmq: 3, ibmmq: 3, solace: 4, pulsar: 5 },
  { criterion: "Durability",         kafka: 5, rabbitmq: 4, ibmmq: 5, solace: 5, pulsar: 5 },
  { criterion: "Ordering",           kafka: 5, rabbitmq: 3, ibmmq: 4, solace: 4, pulsar: 4 },
  { criterion: "Operability",        kafka: 3, rabbitmq: 4, ibmmq: 4, solace: 4, pulsar: 3 },
  { criterion: "Stream processing",  kafka: 5, rabbitmq: 0, ibmmq: 0, solace: 2, pulsar: 4 },
  { criterion: "Geo-replication",    kafka: 4, rabbitmq: 2, ibmmq: 4, solace: 5, pulsar: 5 },
  { criterion: "Cost / TCO",         kafka: 3, rabbitmq: 4, ibmmq: 2, solace: 2, pulsar: 4 },
  { criterion: "Ecosystem",          kafka: 5, rabbitmq: 3, ibmmq: 3, solace: 3, pulsar: 3 },
  { criterion: "Siemens AIG fit",    kafka: 5, rabbitmq: 3, ibmmq: 3, solace: 3, pulsar: 4 },
];

export const ESB_ANTI_PATTERNS = [
  { name: "Sync everything",          detail: "Calling APIs synchronously across the bus. Tight coupling, cascading failures, blocked threads." },
  { name: "Persistence as afterthought", detail: "'We'll add durability later.' Without persistence, messages die in broker memory on any restart." },
  { name: "Non-idempotent consumers", detail: "Receiver assumes exactly-once and double-charges, double-confirms, or duplicate-creates." },
  { name: "DLQ as graveyard",         detail: "Dead-letter queue with no owner, no replay, no metrics. Errors accumulate forever in silence." },
  { name: "Schema by convention",     detail: "No registry. Producers and consumers drift independently. Production breaks on next deploy." },
  { name: "SPOF broker",              detail: "Single broker node, single AZ, no replication. One disk failure = total outage." },
  { name: "Tracing-by-grep",          detail: "No correlation IDs, no distributed tracing. Debugging an issue means logging into 5 servers." },
  { name: "Untested failures",        detail: "Happy path tested, failure paths assumed. Retries, DLQ, replays never exercised before prod." },
];
